/**
 * Translation bindings on a tag (metaplan A6 / A10; AIE §8 "Unbind on direct edit"). `GET /tour_tag/{id}/`
 * carries `translation_bindings`: `role: target` — a leaf of this tag is auto-translated from `origin`; an
 * edit of it is replaced at the next origin change unless the binding is removed; `role: origin` — a leaf of
 * this tag feeds other translations. Addresses are JSON pointers inside `data` plus a language: a
 * multilingual leaf is `{iso: value}` at the pointer.
 */
import { Chip, Stack, Tooltip } from '@mui/material';
import * as React from 'react';

/** The top-level `data` key a pointer lives under (`/meeting_point/instructions` → `meeting_point`). */
export const fieldOfPath = (path) => String(path || '').split('/').filter(Boolean)[0] || '';

const unescape = (seg) => seg.replace(/~1/g, '/').replace(/~0/g, '~');

/** The value of one leaf: `data` at the pointer, then the language when the value is `{iso: value}`. */
export const leafValue = (data, path, lang) => {
    let node = data;
    for (const seg of String(path || '').split('/').filter(Boolean).map(unescape)) {
        if (node === null || node === undefined || typeof node !== 'object') return undefined;
        node = node[seg];
    }
    if (node && typeof node === 'object' && !Array.isArray(node) && lang && lang in node) {
        return node[lang];
    }
    return node;
};

export const bindingsByField = (bindings) => {
    const targets = {};
    const origins = {};
    (bindings || []).forEach((b) => {
        if (b.role === 'target') {
            const f = fieldOfPath(b.target_path);
            (targets[f] = targets[f] || []).push(b);
        } else if (b.role === 'origin') {
            const f = fieldOfPath(b.origin_path);
            (origins[f] = origins[f] || []).push(b);
        }
    });
    return { targets, origins };
};

/** The target bindings whose leaf this edit changes (the editor asks "keep or unbind?" for these). */
export const changedBoundTargets = (bindings, before, after) =>
    (bindings || []).filter(
        (b) => b.role === 'target'
            && JSON.stringify(leafValue(before || {}, b.target_path, b.target_lang) ?? null)
                !== JSON.stringify(leafValue(after || {}, b.target_path, b.target_lang) ?? null),
    );

/** The PUT body: never echoes `translation_bindings`; adds `translation_unbind` when asked to. */
export const tagSaveBody = (tag, unbindIds = []) => {
    // eslint-disable-next-line no-unused-vars
    const { translation_bindings: _bindings, ...rest } = tag;
    return unbindIds.length ? { ...rest, translation_unbind: unbindIds } : rest;
};

const statusText = (b) => [
    `Status: ${b.status}`,
    b.last_synced_ts ? `last synced ${b.last_synced_ts}` : 'not synced yet',
    b.last_error ? `error: ${b.last_error}` : '',
].filter(Boolean).join(' · ');

/** The chips next to a `data` field: "auto-translated from …" (targets), "feeds N translations" (origins). */
export const BindingChips = ({ field, index }) => {
    const targets = index.targets[field] || [];
    const origins = index.origins[field] || [];
    if (!targets.length && !origins.length) return null;
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {targets.map((b) => (
                <Tooltip key={b.id} title={statusText(b)}>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={b.status === 'active' ? 'info' : 'warning'}
                        label={`${b.target_lang}: auto-translated from ${b.origin_label} (${b.origin_lang})`}
                        data-testid={`binding-target-${b.id}`}
                    />
                </Tooltip>
            ))}
            {origins.length > 0 && (
                <Tooltip title={origins.map((b) => `${b.target_label} (${b.target_lang})`).join(', ')}>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`Feeds ${origins.length} translation${origins.length === 1 ? '' : 's'}`}
                        data-testid={`binding-origin-${field}`}
                    />
                </Tooltip>
            )}
        </Stack>
    );
};
